import { sanitizeFormValues } from './formSanitizer.js';

let initialized = false;

// Central selectors and helpers
const TARGET_SELECTOR = 'input[type="text"], input[type="url"], textarea';
const SUBMIT_SELECTOR =
  'button[type="submit"], .ant-modal-footer .ant-btn-primary, .ant-drawer-footer .ant-btn-primary, .ant-btn.ant-btn-primary.jobForm_submitButton';

// Simple helper to detect HTML tags (replaces the problematic regex)
function containsHtmlTags(value) {
  const doc = new DOMParser().parseFromString(value, 'text/html');
  return doc.body.children.length > 0;
}

const FORBIDDEN_PATTERNS = [
  /<script\b[^>]*>[\s\S]*?<\/script>/gi,
  /javascript:/gi,
  /vbscript:/gi,
  /data:\s*text\/html/gi,
  /on\w+=/gi,
  /alert\(/gi,
  /document\.cookie/gi,
  /eval\(/gi,
  /expression\(/gi,
  /--/g,
  /;/g,
  /drop\s+table/gi,
  /union\s+select/gi,
  /insert\s+into/gi,
  /update\s+\w+\s+set/gi,
  /delete\s+from/gi,
  /\b(or|and)\b\s+\d+=\d+/gi,
  /^["']/, // Quotes at start
  /["']$/, // Quotes at end
  /"{2,}/, // Multiple double quotes
  /'{2,}/, // Multiple single quotes
  /'\s|[\s,.]'/, // Single quotes after space or before space/punctuation (except for contractions)
];

function isTargetedElement(element) {
  if (!element || element.disabled || element.readOnly) return false;
  const tag = (element?.tagName || '').toLowerCase();
  if (tag === 'input') {
    const type = (element?.getAttribute?.('type') || 'text').toLowerCase();
    return ['text', 'url'].includes(type);
  }
  return tag === 'textarea';
}

function getContainerFrom(el) {
  const footer = el.closest?.('.jobForm_footerButtons');
  if (footer) {
    const card = footer.closest('.jobForm_card');
    if (card) {
      const form = card.querySelector('form');
      if (form) return form;
    }
    return document;
  }
  const jobForm = el.closest?.('.jobForm_footerButtons')?.closest('form');
  if (jobForm) return jobForm;
  return el.closest?.('form') || el.closest?.('.ant-modal, .ant-drawer') || document;
}

const syncTimers = new WeakMap();

function getTargetFields(container) {
  return Array.from(container?.querySelectorAll?.(TARGET_SELECTOR) || []);
}

function getSubmitButtons(container) {
  if (!container?.querySelectorAll) return [];

  let buttons = Array.from(container.querySelectorAll(SUBMIT_SELECTOR));

  const isForm = container.tagName?.toLowerCase() === 'form';
  if (isForm) {
    const modal = container.closest?.('.ant-modal, .ant-drawer');
    if (modal) {
      buttons = buttons.concat(Array.from(modal.querySelectorAll(SUBMIT_SELECTOR)));
    } else {
      const card = container.closest('.jobForm_card');
      if (card) {
        buttons = buttons.concat(Array.from(card.querySelectorAll(SUBMIT_SELECTOR)));
      }
    }
  }
  const isModal = container.matches?.('.ant-modal, .ant-drawer');
  if (isModal) {
    const form = container.querySelector?.('form');
    if (form) {
      buttons = buttons.concat(Array.from(form.querySelectorAll('button[type="submit"]')));
    }
  } else {
    const form = container.closest?.('form');
    if (form) {
      buttons = buttons.concat(Array.from(form.querySelectorAll('button[type="submit"]')));
    }
  }
  return Array.from(new Set(buttons));
}

// Inline error rendering
const fieldToErrorNode = new WeakMap();

function ensureErrorNode(element) {
  let node = fieldToErrorNode.get(element);
  if (node?.isConnected) return node;
  node = document.createElement('div');
  node.setAttribute('data-tt-inline-error', '1');
  node.style.color = '#ff4d4f';
  node.style.fontSize = '14px';
  node.style.lineHeight = '1.4';
  node.style.marginTop = '5px';
  const antFormItem = element?.closest?.('.ant-form-item');
  if (antFormItem) {
    const helpContainer = antFormItem.querySelector('.ant-form-item-explain') || antFormItem;
    helpContainer.appendChild(node);
  } else {
    element.insertAdjacentElement('afterend', node);
  }
  fieldToErrorNode.set(element, node);
  return node;
}

function setFieldValidity(element, errorText) {
  try {
    const currentlyInvalid = element?.dataset?.malicious === '1';
    const nextInvalid = Boolean(errorText);

    if (nextInvalid) {
      applyInvalidState(element, errorText, currentlyInvalid);
    } else if (currentlyInvalid) {
      clearInvalidState(element);
    }
  } catch {
    // fail silently
  }
}

function applyInvalidState(element, errorText, currentlyInvalid) {
  const node = ensureErrorNode(element);
  const message = errorText || 'Please enter a valid value.';

  if (node.textContent !== message) {
    node.textContent = message;
  }

  if (currentlyInvalid) return;

  if (element?.setAttribute) {
    element.setAttribute('aria-invalid', 'true');
  }

  const antFormItem = element?.closest?.('.ant-form-item');
  if (antFormItem) {
    antFormItem.classList.add('ant-form-item-has-error');
  }

  if (element?.dataset) {
    element.dataset.malicious = '1';
  }
}

function clearInvalidState(element) {
  const node = fieldToErrorNode.get(element);
  if (node?.isConnected && node.textContent) {
    node.textContent = '';
  }

  if (element?.removeAttribute) {
    element.removeAttribute('aria-invalid');
  }

  const antFormItem = element?.closest?.('.ant-form-item');
  if (antFormItem) {
    antFormItem.classList.remove('ant-form-item-has-error');
  }

  if (element?.dataset) {
    delete element.dataset.malicious;
  }
}

function validateValue(element) {
  const value = element.value || '';
  if (typeof value !== 'string' || !value) return null;

  // Check for malicious patterns
  if (typeof value === 'string' && (containsHtmlTags(value) || FORBIDDEN_PATTERNS.some(p => p.test(value)))) {
    return 'Please enter a valid value.';
  }

  return null;
}

function computeInvalid(container, showInlineErrors) {
  const fields = getTargetFields(container);
  let anyInvalid = false;
  for (const field of fields) {
    if (!isTargetedElement(field)) continue;
    const err = validateValue(field) || (field.dataset?.malicious ? 'Please enter a valid value.' : null);
    if (err) {
      anyInvalid = true;
      if (showInlineErrors) setFieldValidity(field, err);
    } else if (showInlineErrors) {
      setFieldValidity(field, null);
    }
  }
  return anyInvalid;
}

function syncSubmitButtons(container) {
  const invalid = computeInvalid(container, false);
  for (const btn of getSubmitButtons(container)) {
    try {
      if (invalid) {
        if (!btn.disabled) {
          btn.disabled = true;
          btn.setAttribute('data-disabled-by-guard', 'true');
        }
      } else if (btn.getAttribute('data-disabled-by-guard') === 'true') {
        btn.disabled = false;
        btn.removeAttribute('data-disabled-by-guard');
      }

      if (btn.disabled) {
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.removeAttribute('aria-disabled');
      }
    } catch (err) {
      console.error('Failed to sync submit button state:', err);
    }
  }
}

function scheduleSync(container) {
  try {
    const existing = syncTimers.get(container);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      syncTimers.delete(container);
      syncSubmitButtons(container);
    }, 100);
    syncTimers.set(container, timer);
  } catch (err) {
    console.error('Failed to schedule guard sync:', err);
  }
}

export function initializeGlobalInputGuard() {
  if (initialized) return;
  initialized = true;

  // Live validate and sync buttons
  document.addEventListener(
    'input',
    event => {
      const target = event.target;
      if (!isTargetedElement(target)) return;
      const err = validateValue(target);
      setFieldValidity(target, err);
      const container = getContainerFrom(target);
      scheduleSync(container);
    },
    true
  );

  // Block native form submissions if invalid; sanitize on success
  document.addEventListener(
    'submit',
    event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const hasInvalid = computeInvalid(form, true);
      if (hasInvalid) {
        event.stopPropagation();
        event.preventDefault();
        syncSubmitButtons(form);
        return;
      }
      try {
        const fields = getTargetFields(form);
        const payload = {};
        for (const [index, el] of fields.entries()) {
          if (!isTargetedElement(el)) continue;
          const key = el.name || `__tt_field_${index}`;
          payload[key] = el.value != null ? el.value : '';
        }
        const sanitized = sanitizeFormValues(payload);
        for (const [index, el] of fields.entries()) {
          if (!isTargetedElement(el)) continue;
          const key = el.name || `__tt_field_${index}`;
          if (Object.hasOwn(sanitized, key)) el.value = sanitized[key];
        }
      } catch (err) {
        console.error('Failed to sanitize form submission:', err);
      }
    },
    true
  );

  // Guard submit-like button clicks (AntD modal/drawer primary buttons)
  document.addEventListener(
    'click',
    event => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      const submitEl = target.closest?.(SUBMIT_SELECTOR);
      if (!submitEl) return;
      const container = getContainerFrom(submitEl);
      if (computeInvalid(container, true)) {
        event.stopPropagation();
        event.preventDefault();
        syncSubmitButtons(container);
      }
    },
    true
  );
}
